import { NavLink, Outlet } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { ImpersonationBanner } from "./ImpersonationBanner";
import {
  Activity, BookOpen, ClipboardCheck, ClipboardList, Database, Download, Globe,
  LayoutDashboard, LogOut, MessageSquare, ShoppingCart, Sparkles, Users,
} from "lucide-react";

const NAV = [
  { to: "/superadmin", end: true, key: "superadmin.overview", icon: Activity, testId: "sa-nav-overview" },
  { to: "/superadmin/users", key: "superadmin.users", icon: Users, testId: "sa-nav-users" },
  { to: "/superadmin/logins", key: "superadmin.logins", icon: Globe, testId: "sa-nav-logins" },
  { to: "/superadmin/enrollments", key: "superadmin.enrollments", icon: BookOpen, testId: "sa-nav-enrollments" },
  { to: "/superadmin/orders", key: "superadmin.orders", icon: ShoppingCart, testId: "sa-nav-orders" },
  { to: "/superadmin/exams", key: "superadmin.exams", icon: ClipboardList, testId: "sa-nav-exams" },
  { to: "/superadmin/certificates", key: "superadmin.certificates", icon: ClipboardCheck, testId: "sa-nav-certs" },
  { to: "/superadmin/sparky", key: "superadmin.sparky", icon: MessageSquare, testId: "sa-nav-sparky" },
  { to: "/superadmin/content", key: "superadmin.content", icon: Database, testId: "sa-nav-content" },
  { to: "/superadmin/exports", key: "superadmin.exports", icon: Download, testId: "sa-nav-exports" },
];

export function SuperAdminShell() {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-[#FDFBF7]" dir={lang === "ar" ? "rtl" : "ltr"} data-testid="superadmin-shell">
      <ImpersonationBanner />
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr]">
        <aside className="border-r border-black/5 bg-white/60 backdrop-blur-xl md:min-h-screen p-4 md:p-6">
          <div className="flex items-center gap-2 mb-8">
            <span className="inline-block h-9 w-9 rounded-2xl bg-[#1A1A1A] grid place-items-center text-white shadow-[0_3px_0_#000]">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <div className="font-black text-lg leading-none">{lang === "ar" ? "سباركل" : "Sparkl"}</div>
              <div className="text-xs font-bold text-[#F72585]">{t("superadmin.nav")}</div>
            </div>
          </div>

          <nav className="flex flex-col gap-1" data-testid="sa-sidebar">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                    isActive
                      ? "bg-[#1A1A1A] text-white shadow-[0_3px_0_#000]"
                      : "text-black/70 hover:bg-black/5"
                  }`
                }
                data-testid={item.testId}
              >
                <item.icon className="h-4 w-4" />
                <span>{t(item.key)}</span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-8 space-y-2 border-t border-black/5 pt-4">
            <NavLink
              to="/admin/courses"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold text-black/60 hover:bg-black/5"
              data-testid="sa-nav-admin"
            >
              <Sparkles className="h-4 w-4" />
              {t("admin.nav")}
            </NavLink>
            <NavLink
              to="/dashboard"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold text-black/60 hover:bg-black/5"
              data-testid="sa-back-dashboard"
            >
              <LayoutDashboard className="h-4 w-4" />
              {lang === "ar" ? "لوحة التعلّم" : "Learner dashboard"}
            </NavLink>
            <button
              onClick={toggle}
              className="w-full text-start flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold text-black/60 hover:bg-black/5"
              data-testid="sa-lang-toggle"
            >
              <span className="text-base">🌐</span> {t("lang.toggle")}
            </button>
            <button
              onClick={logout}
              className="w-full text-start flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold text-black/60 hover:bg-black/5"
              data-testid="sa-logout"
            >
              <LogOut className="h-4 w-4" />
              {t("dashboard.logout")}
            </button>
          </div>

          <div className="mt-6 text-xs text-black/50">
            <div className="font-bold text-black/70">{user?.name}</div>
            <div>{user?.email}</div>
            <div className="mt-1 inline-block rounded-full bg-[#F72585]/20 text-[#B00060] px-2 py-0.5 font-bold">
              {user?.role}
            </div>
          </div>
        </aside>

        <main className="p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
